import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type ColumnSchema, type Template } from '../lib/api';
import { SchemaEditor } from '../components/SchemaEditor';
import { parseCsvHeader } from '../lib/csv';
import { buildSchemaFromHeaders } from '../lib/smart-mapping';
import type { useAuth } from '../auth/useAuth';

type Auth = ReturnType<typeof useAuth>;

const DEFAULT_COUNT = 100;

export function GeneratorPage({ auth }: { auth: Auth }) {
  const { templateId: routeId } = useParams<{ templateId?: string }>();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(
    routeId ? Number(routeId) : null,
  );
  const [schema, setSchema] = useState<ColumnSchema[]>([]);
  const [name, setName] = useState<string>('Ad-hoc');
  const [count, setCount] = useState<number>(DEFAULT_COUNT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [headerInput, setHeaderInput] = useState<string>('');
  const [showHeaderPaste, setShowHeaderPaste] = useState(false);

  useEffect(() => {
    api.listTemplates()
      .then((r) => setTemplates(r.templates))
      .catch((e) => setError((e as Error).message));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setBusy(true);
    api.getTemplate(selectedId)
      .then((r) => {
        setSchema(r.template.schema);
        setName(r.template.name);
        setError(null);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false));
  }, [selectedId]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedId) || null,
    [templates, selectedId],
  );

  const canEditSchema =
    !selectedTemplate ||
    (!selectedTemplate.is_builtin &&
      (auth.canEdit || selectedTemplate.created_by.toLowerCase() === auth.user?.email.toLowerCase()));

  const handlePickTemplate = (id: number | null) => {
    setSelectedId(id);
    if (id) navigate(`/generate/${id}`);
    else navigate('/generate');
    if (!id) {
      setSchema([]);
      setName('Ad-hoc');
    }
  };

  const handlePasteHeader = () => {
    const lines = headerInput.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    let headers: string[] = [];
    if (lines.length === 1) {
      headers = parseCsvHeader(lines[0]);
    } else {
      headers = lines;
    }
    if (headers.length === 0) {
      setError('No headers detected.');
      return;
    }
    setSchema(buildSchemaFromHeaders(headers));
    setShowHeaderPaste(false);
    setHeaderInput('');
    setSelectedId(null);
    setName('Pasted header');
    navigate('/generate');
    setInfo(`Loaded ${headers.length} columns and applied smart Mockaroo-type guesses.`);
  };

  const handleGenerate = async () => {
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      const blob = await api.generate({ schema, count, template_id: selectedId ?? null });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeName = (name || 'mockaroo').replace(/[^a-zA-Z0-9_-]+/g, '_').toLowerCase();
      a.href = url;
      a.download = `${safeName}-${count}rows-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setInfo(`Generated ${count} rows. Downloaded.`);
    } catch (e) {
      setError((e as Error).message || 'Generation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ gap: '1.25rem' }}>
      <header className="row" style={{ alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ marginBottom: '0.2em' }}>Generate CSV</h1>
          <p className="muted" style={{ margin: 0 }}>
            Pick a template, paste a CSV header row, or start blank. Each generation calls Mockaroo with the
            server-side API key.
          </p>
        </div>
      </header>

      {error && <div className="banner error">{error}</div>}
      {info && <div className="banner ok">{info}</div>}

      <section className="card stack">
        <div className="row" style={{ gap: '1rem', flexWrap: 'wrap' }}>
          <label style={{ flex: '1 1 280px' }}>
            <div className="muted" style={{ marginBottom: 4, fontSize: '0.85em' }}>Template</div>
            <select
              value={selectedId ?? ''}
              onChange={(e) => handlePickTemplate(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">(none — ad-hoc)</option>
              <optgroup label="Built-in">
                {templates.filter((t) => t.is_builtin).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </optgroup>
              <optgroup label="Saved">
                {templates.filter((t) => !t.is_builtin).map((t) => (
                  <option key={t.id} value={t.id}>{t.name} · {t.created_by}</option>
                ))}
              </optgroup>
            </select>
          </label>

          <label style={{ flex: '0 0 130px' }}>
            <div className="muted" style={{ marginBottom: 4, fontSize: '0.85em' }}>Row count (1–5000)</div>
            <input
              type="number"
              min={1}
              max={5000}
              value={count}
              onChange={(e) => setCount(Math.min(5000, Math.max(1, Number(e.target.value))))}
            />
          </label>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
            <button type="button" onClick={() => setShowHeaderPaste((v) => !v)}>
              {showHeaderPaste ? 'Cancel' : 'Paste CSV header…'}
            </button>
            <button
              type="button"
              className="primary"
              onClick={handleGenerate}
              disabled={busy || schema.length === 0}
              title={schema.length === 0 ? 'Add at least one column' : ''}
            >
              {busy ? 'Generating…' : `Generate ${count.toLocaleString()} rows`}
            </button>
          </div>
        </div>

        {showHeaderPaste && (
          <div className="stack" style={{ gap: 6 }}>
            <textarea
              placeholder={'Paste either a single header line (e.g. "Site Name,PBX Name,Address,…") or one column name per line.'}
              value={headerInput}
              onChange={(e) => setHeaderInput(e.target.value)}
              rows={4}
            />
            <div className="row" style={{ gap: '0.5rem' }}>
              <button type="button" className="primary" onClick={handlePasteHeader} disabled={!headerInput.trim()}>
                Apply smart mapping
              </button>
              <span className="dim">Smart mapping uses column-name heuristics to pre-pick Mockaroo types.</span>
            </div>
          </div>
        )}

        {selectedTemplate?.is_builtin && (
          <div className="banner">
            <strong className="tag builtin">built-in</strong> · This template is read-only for non-admins.
            {auth.canEdit && (
              <>
                {' '}Save a copy via <a onClick={() => navigate('/templates/new')} style={{ cursor: 'pointer' }}>New template</a> to customise.
              </>
            )}
          </div>
        )}
      </section>

      <section className="card">
        <SchemaEditor schema={schema} onChange={setSchema} disabled={!canEditSchema} />
      </section>
    </div>
  );
}
